/**
 * The one list of games that BOTH the client and the server agree on.
 *
 * Deliberately its own package rather than part of `@arena/sim`, for two
 * reasons. The hub page must stay light: importing the simulation to find out
 * whether a lower score is better would drag PixiJS-adjacent code into a bundle
 * whose whole point is opening instantly. And this is not simulation, it is
 * metadata; putting it in the sim package would make "sim" mean "shared", which
 * is how packages stop meaning anything.
 *
 * What lives here is only what both sides need: the id, how to score it, and
 * the bounds a submitted score must fall inside. Everything about how a game
 * LOOKS (icon, colour, blurb, how to play, the mount function) stays in the
 * client catalogue, and everything about how it RUNS stays in the game module.
 */
import { DEFAULT_LEVEL } from './levels.js';
export * from './elo.js';
export * from './levels.js';
export * from './skins.js';
/**
 * Solo games. `min`/`max` are the believable range for a single result.
 *
 * The maxima are set well above anything a strong player reaches, because the
 * cost of a bound being slightly too loose is a suspicious entry, and the cost
 * of it being slightly too tight is a real record thrown away.
 */
const SOLO = [
    { id: 'two-zero-four-eight', title: '2048', higherIsBetter: true, unit: 'points', min: 0, max: 400_000, levels: true },
    { id: 'aim-trainer', title: 'Aim Trainer', higherIsBetter: true, unit: 'points', min: 0, max: 4_000, levels: true },
    { id: 'anagram', title: 'Anagrammes', higherIsBetter: true, unit: 'points', min: 0, max: 2_000, levels: true },
    { id: 'blackjack', title: 'Blackjack', higherIsBetter: true, unit: 'jetons', min: 0, max: 100_000, levels: true },
    { id: 'click-speed', title: 'Vitesse de clic', higherIsBetter: true, unit: 'clics', min: 0, max: 250, levels: false },
    { id: 'color-match', title: 'Color Match', higherIsBetter: true, unit: 'points', min: 0, max: 1_000, levels: true },
    { id: 'flappy-bird', title: 'Flappy', higherIsBetter: true, unit: 'tuyaux', min: 0, max: 10_000, levels: true },
    { id: 'hangman', title: 'Le Pendu', higherIsBetter: false, unit: 'erreurs', min: 0, max: 7, levels: true },
    { id: 'math-sprint', title: 'Math Sprint', higherIsBetter: true, unit: 'points', min: 0, max: 20_000, levels: true },
    { id: 'memory', title: 'Memory', higherIsBetter: false, unit: 'coups', min: 8, max: 500, levels: true },
    { id: 'minesweeper', title: 'Demineur', higherIsBetter: false, unit: 's', min: 3, max: 3_600, levels: true },
    { id: 'pong', title: 'Pong', higherIsBetter: true, unit: 'echanges', min: 0, max: 5_000, levels: true },
    { id: 'quiz', title: 'Quiz Culture', higherIsBetter: true, unit: '/ 10', min: 0, max: 10, levels: true },
    // 80ms is quicker than a human nerve can carry the signal, so anything under
    // it was not a reaction. The upper bound is a distracted but genuine attempt.
    { id: 'reaction-time', title: 'Temps de reaction', higherIsBetter: false, unit: 'ms', min: 80, max: 10_000, levels: false },
    { id: 'rock-paper-scissors', title: 'Pierre Feuille Ciseaux', higherIsBetter: true, unit: 'victoires', min: 0, max: 1_000, levels: true },
    { id: 'simon-says', title: 'Simon', higherIsBetter: true, unit: 'tours', min: 0, max: 200, levels: true },
    { id: 'snake', title: 'Snake', higherIsBetter: true, unit: 'points', min: 0, max: 4_000, levels: true },
    { id: 'speed-typing', title: 'Frappe rapide', higherIsBetter: true, unit: 'mpm', min: 0, max: 250, levels: true },
    { id: 'tic-tac-toe', title: 'Morpion', higherIsBetter: true, unit: 'nuls', min: 0, max: 1_000, levels: true },
    { id: 'wordle', title: 'Motus', higherIsBetter: false, unit: 'essais', min: 1, max: 8, levels: true },
];
/**
 * Online games. Ranked by rating rather than by a best score, so the bounds are
 * unused; they are filled in only so one type describes every game.
 */
const ONLINE = [
    {
        id: 'deathmatch',
        title: 'Arena Deathmatch',
        higherIsBetter: true,
        unit: 'points',
        min: 0,
        max: 100_000,
        levels: true,
    },
    {
        id: 'crown',
        title: 'King of the Crown',
        higherIsBetter: true,
        unit: 'points',
        min: 0,
        max: 100_000,
        levels: true,
    },
];
export const GAMES = [
    ...SOLO.map((g) => ({ ...g, kind: 'solo' })),
    ...ONLINE.map((g) => ({ ...g, kind: 'online' })),
];
const BY_ID = new Map(GAMES.map((g) => [g.id, g]));
export function gameById(id) {
    return BY_ID.get(id) ?? null;
}
export function isOnline(id) {
    return BY_ID.get(id)?.kind === 'online';
}
/**
 * Whether this game actually offers the given difficulty.
 *
 * Checked on the write path, so a game with no honest difficulty knob cannot
 * be given a second leaderboard by anyone who fancies posting to one.
 */
export function levelIsAllowed(id, level) {
    const meta = BY_ID.get(id);
    if (!meta)
        return false;
    return meta.levels || level === DEFAULT_LEVEL;
}
/**
 * Whether a submitted score is worth storing.
 *
 * Rejects the unknown game, the non finite number, and anything outside the
 * believable range. Everything that reaches the database has passed this.
 */
export function scoreIsPlausible(gameId, value) {
    const game = BY_ID.get(gameId);
    if (!game || game.kind !== 'solo')
        return false;
    if (typeof value !== 'number' || !Number.isFinite(value))
        return false;
    return value >= game.min && value <= game.max;
}
/** True when `a` is a better result than `b` at this game. */
export function isBetter(gameId, a, b) {
    const game = BY_ID.get(gameId);
    if (!game)
        return false;
    return game.higherIsBetter ? a > b : a < b;
}
//# sourceMappingURL=index.js.map